import * as Sentry from "@sentry/vue";

/**
 * Unified error handler for all API operations
 * @param {Function} asyncFunc - async function to execute
 * @param {Object} [options] - options object
 * @param {string} [options.errorMessage] - custom error message fallback
 * @param {string} [options.successMessage] - custom success message
 * @param {Function} [options.onSuccess] - success callback func
 * @param {Function} [options.onError] - error callback func
 * @param {Function} [options.onFinally] - final callback func
 * @param {boolean|Function} [options.throwOnError] - rethrow the normalized error
 *   (after onError/Sentry) instead of returning it, so a wrapping
 *   <ErrorBoundary> can catch it via onErrorCaptured. Pass a predicate
 *   `(error) => boolean` to throw selectively (e.g. spare a 422 handled locally).
 * @returns {Promise<any>} - result from API
 */
export const handleRequest = async (asyncFunc, options = {}) => {

  const {
    errorMessage = 'An unexpected error occurred.',
    successMessage,
    onSuccess,
    onError,
    onFinally,
    throwOnError = false
  } = options;

  const shouldThrow = (err) =>
    typeof throwOnError === 'function' ? throwOnError(err) : throwOnError;

  let result;
  try {
    result = await asyncFunc();

    // Handle standardized API response format
    if (typeof result?.success === 'boolean') {

      if (result.success) {
        if (onSuccess) onSuccess(result);
        return result;
      } else {
        // API returned success: false
        if (onError) onError(result);
        Sentry.captureException(result);
        if (shouldThrow(result)) throw result;
        return result;
      }
    }

    // Fallback for non-standardized responses
    if (onSuccess) onSuccess(result);
    return result;

  } catch (error) {
    // Our own rethrow from the standardized success:false branch above —
    // already normalized and reported, pass it through untouched.
    if (result !== undefined && error === result) throw error;

    const responseData = error?.response?.data;
    const statusCode = error?.response?.status;

    // Check if error response follows the standardized format
    if (responseData?.success === false) {
      const errorWithStatusCode = { ...responseData, status: statusCode };

      if (onError) onError(errorWithStatusCode);
      Sentry.captureException(error);
      if (shouldThrow(errorWithStatusCode)) throw errorWithStatusCode;
      return errorWithStatusCode;
    }
    // Network error or unexpected failure

    const enrichedNetworkError = { success: false, message: errorMessage, status: statusCode, error };
    if (onError) onError(enrichedNetworkError);
    Sentry.captureException(enrichedNetworkError);

    if (shouldThrow(enrichedNetworkError)) throw enrichedNetworkError;
    return enrichedNetworkError;
  } finally {
    if (onFinally) onFinally();
  }
};
